import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import FilterChipRail from './FilterChipRail';
import { pickPillColumnFilters } from '../constants/pillFilters';

/**
 * TD-2465 — the pill-filter chip rail, shared by the SearchResults page and the
 * Projects results view.
 *
 * Surfaces the active pill filters as a grouped, removable chip rail (with the
 * per-column All/Any toggle). Renders nothing unless it's `visible` (List view,
 * results loaded) and at least one pill filter is active.
 *
 * `variant` drives the desktop/mobile CSS hook: SearchResults mounts a desktop
 * instance inside its (mobile-hidden) header plus a mobile instance outside it,
 * while the Projects view mounts a single desktop instance (its header stays
 * visible on mobile).
 */
const PillFilterRail = observer(({ store, variant, visible }) => {
  if (!visible) return null;
  const pillColumnFilters = pickPillColumnFilters(store.columnFilters);
  if (Object.keys(pillColumnFilters).length === 0) return null;

  // Clear exactly what this rail displays. It becomes visible as soon as ONE
  // pill filter is active, so spotlights and non-keyword column filters can be
  // active while invisible here — a global reset would silently drop filter
  // state the user never saw in the control they clicked. The unified rail owns
  // the global "Clear all", because it renders a chip for every filter type.
  const clearAll = () =>
    Object.keys(pillColumnFilters).forEach((key) => store.clearColumnFilter(key));

  return (
    <div className={`profiler-search-filter-rail profiler-search-filter-rail--${variant}`}>
      <FilterChipRail
        columnFilters={pillColumnFilters}
        onSetColumnFilter={store.setColumnFilter}
        onClearColumnFilter={store.clearColumnFilter}
        onSetPillMode={store.setPillFilterMode}
        onClearAll={clearAll}
      />
    </div>
  );
});

PillFilterRail.propTypes = {
  // MobX store exposing columnFilters + setColumnFilter / clearColumnFilter /
  // setPillFilterMode (both the SearchResults and Projects stores qualify).
  store: PropTypes.object.isRequired,
  variant: PropTypes.string.isRequired,
  visible: PropTypes.bool
};

export default PillFilterRail;
